import React, { useCallback, useEffect, useState, useRef, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSupplierStore, JobDetails, SupplierRequest } from '../../lib/supplierStore';
import { toast } from 'sonner';
import { Navigation, ArrowRight, Check, X, Clock, Car, Wrench, Home, Sofa, AlertTriangle, Snowflake, Package, TrendingDown, Image as ImageIcon, ChevronLeft, ChevronRight, ChevronDown, ZoomIn, Sparkles, Send, MessageCircle, Bot, History, Repeat } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SupplierLayout } from '../../components/SupplierLayout';
import { AskCustomerAiSlider } from '../../components/AskCustomerAiSlider';
// ================= Helper Components ===================
// CounterEntry component: displays a vertical list with collapsible entries showing name, amount, delta, timestamp, and optional note
function CounterEntry({
  entries
}: {
  entries: Array<{
    name: string;
    amount: number;
    delta?: number;
    timestamp: string;
    note?: string;
  }>;
}) {
  return <ol className="relative pl-7 space-y-3 before:absolute before:left-[11px] before:top-1 before:bottom-1 before:w-px before:bg-amber-200" data-id="element-4180">
      {entries.map((entry, index) => {
      const isYou = entry.name === 'You';
      return <motion.li key={index} initial={{
        opacity: 0,
        x: -4
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: index * 0.04
      }} className="relative" data-id="element-4181">
            <div className={`absolute -left-7 top-1 w-6 h-6 rounded-full flex items-center justify-center font-bold text-[9px] ring-2 ring-white shrink-0 ${isYou ? 'bg-gray-900 text-white' : 'bg-brand-100 text-brand-700'}`} data-id="element-4182">
              {isYou ? 'You' : entry.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex items-baseline gap-2 flex-wrap" data-id="element-4183">
              <span className="text-xs font-medium text-gray-700" data-id="element-4184">
                {entry.name} countered
              </span>
              <span className="text-sm font-bold text-gray-900" data-id="element-4185">
                AED {entry.amount.toLocaleString()}
              </span>
              {entry.delta !== undefined && entry.delta !== 0 && <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${entry.delta >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-id="element-4186">
                  {entry.delta >= 0 ? '+' : ''}
                  {entry.delta.toLocaleString()} AED
                </span>}
              <span className="text-[10px] text-gray-400 ml-auto" data-id="element-4187">
                {entry.timestamp}
              </span>
            </div>
            {entry.note && <p className="mt-0.5 text-[11px] italic text-gray-500 leading-snug" data-id="element-4188">
                "{entry.note}"
              </p>}
          </motion.li>;
    })}
    </ol>;
}
// CounterInput component: input for amount and optional note, with cancel and submit buttons
function CounterInput({
  defaultAmount,
  onCancel,
  onSubmit
}: {
  defaultAmount: number;
  onCancel: () => void;
  onSubmit: (amount: number, note?: string) => void;
}) {
  const [amount, setAmount] = useState<string>(defaultAmount.toString());
  const [note, setNote] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleSend = () => {
    const parsed = parseInt(amount, 10);
    if (!isNaN(parsed) && parsed > 0) {
      onSubmit(parsed, note.trim() || undefined);
    }
  };
  useEffect(() => {
    // focus input field at mount
    inputRef.current?.focus();
  }, []);
  return <motion.div initial={{
    opacity: 0,
    y: -4
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -4
  }} className="mt-2 p-3 rounded-xl border border-gray-200 bg-gray-50 space-y-2" data-id="element-4189">
      {/* Amount input */}
      <div className="flex items-center border border-gray-300 rounded-full px-2 py-1 bg-gray-50" data-id="element-4190">
        <span className="text-gray-500 font-semibold pr-2" data-id="element-4191">AED</span>
        <input ref={inputRef} type="number" inputMode="numeric" pattern="\d*" className="flex-1 bg-transparent outline-none text-gray-700 text-xl font-semibold px-2 py-0.5" value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} min={1} autoFocus data-id="element-4192" />
      </div>
      {/* Note input */}
      <input type="text" placeholder="Optional note (e.g. Final offer with 2 movers)" className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" value={note} onChange={e => setNote(e.target.value)} data-id="element-4193" />
      {/* Buttons */}
      <div className="flex justify-between mt-2 space-x-2" data-id="element-4194">
        <button className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors" onClick={onCancel} data-id="element-4195">
          Cancel
        </button>
        <button className="flex-1 bg-gray-900 text-white rounded-full px-4 py-2 hover:bg-gray-700 transition-colors" onClick={handleSend} data-id="element-4196">
          Send counter
        </button>
      </div>
    </motion.div>;
}
// =================== Re-inserted Helper Components ===================
function JobDetailsBlock({
  details
}: {
  details: JobDetails;
}) {
  const baseRow = 'flex items-center gap-2 text-sm text-gray-700';
  const label = 'text-[10px] uppercase tracking-wider font-bold text-gray-400';
  switch (details.kind) {
    case 'car':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4197">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4198">
            <Car className="w-3.5 h-3.5 text-brand-600" data-id="element-4199" />
            Vehicle Details
          </div>
          <div className={baseRow} data-id="element-4200">
            <span className="font-bold text-gray-900" data-id="element-4201">
              {details.year} {details.make} {details.model}
            </span>
          </div>
          {details.plate && <div className={baseRow} data-id="element-4202">
              <span className={label} data-id="element-4203">Plate</span>
              <span className="font-medium" data-id="element-4204">{details.plate}</span>
            </div>}
          {details.condition && <div className="text-xs text-gray-600" data-id="element-4205">{details.condition}</div>}
        </div>;
    case 'heavy-equipment':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4206">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4207">
            <Wrench className="w-3.5 h-3.5 text-brand-600" data-id="element-4208" />
            Equipment Details
          </div>
          <div className="font-bold text-gray-900 text-sm" data-id="element-4209">
            {details.equipment}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700" data-id="element-4210">
            <span data-id="element-4211">
              <span className={label + ' mr-1'} data-id="element-4212">Weight</span>
              <span className="font-bold text-gray-900" data-id="element-4213">
                {details.weightTons} tons
              </span>
            </span>
            {details.dimensions && <span data-id="element-4214">
                <span className={label + ' mr-1'} data-id="element-4215">Dims</span>
                <span className="font-medium" data-id="element-4216">{details.dimensions}</span>
              </span>}
          </div>
          {details.permitRequired && <div className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-id="element-4217">
              <AlertTriangle className="w-3 h-3" data-id="element-4218" /> Permit required
            </div>}
        </div>;
    case 'home-move':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4219">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4220">
            <Home className="w-3.5 h-3.5 text-brand-600" data-id="element-4221" />
            Move Details
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700" data-id="element-4222">
            <span data-id="element-4223">
              <span className={label + ' mr-1'} data-id="element-4224">Size</span>
              <span className="font-bold text-gray-900" data-id="element-4225">
                {details.bedrooms} BR
              </span>
            </span>
            {details.floors != null && <span data-id="element-4226">
                <span className={label + ' mr-1'} data-id="element-4227">Floors</span>
                <span className="font-medium" data-id="element-4228">{details.floors}</span>
              </span>}
            <span data-id="element-4229">
              <span className={label + ' mr-1'} data-id="element-4230">Elevator</span>
              <span className="font-medium" data-id="element-4231">
                {details.hasElevator ? 'Yes' : 'No'}
              </span>
            </span>
          </div>
          {details.itemsNote && <div className="text-xs text-gray-600" data-id="element-4232">{details.itemsNote}</div>}
        </div>;
    case 'furniture':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4233">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4234">
            <Sofa className="w-3.5 h-3.5 text-brand-600" data-id="element-4235" />
            Furniture Details
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700" data-id="element-4236">
            <span data-id="element-4237">
              <span className={label + ' mr-1'} data-id="element-4238">Items</span>
              <span className="font-bold text-gray-900" data-id="element-4239">
                {details.itemCount}
              </span>
            </span>
            {details.largestItem && <span data-id="element-4240">
                <span className={label + ' mr-1'} data-id="element-4241">Largest</span>
                <span className="font-medium" data-id="element-4242">{details.largestItem}</span>
              </span>}
          </div>
          {details.assemblyRequired && <div className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full" data-id="element-4243">
              Assembly required
            </div>}
        </div>;
    case 'recovery':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4244">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4245">
            <AlertTriangle className="w-3.5 h-3.5 text-brand-600" data-id="element-4246" />
            Recovery Details
          </div>
          <div className="font-bold text-gray-900 text-sm" data-id="element-4247">
            {details.vehicleType}
          </div>
          <div className="text-xs text-gray-700" data-id="element-4248">
            <span className={label + ' mr-1'} data-id="element-4249">Reason</span>
            <span className="font-medium" data-id="element-4250">{details.reason}</span>
          </div>
          {details.condition && <div className="text-xs text-gray-600" data-id="element-4251">{details.condition}</div>}
        </div>;
    case 'refrigerated':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4252">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4253">
            <Snowflake className="w-3.5 h-3.5 text-brand-600" data-id="element-4254" />
            Cargo Details
          </div>
          <div className="font-bold text-gray-900 text-sm" data-id="element-4255">
            {details.cargoType}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700" data-id="element-4256">
            <span data-id="element-4257">
              <span className={label + ' mr-1'} data-id="element-4258">Weight</span>
              <span className="font-bold text-gray-900" data-id="element-4259">
                {details.weightKg} kg
              </span>
            </span>
            <span data-id="element-4260">
              <span className={label + ' mr-1'} data-id="element-4261">Temp</span>
              <span className="font-bold text-gray-900" data-id="element-4262">
                {details.tempRange}
              </span>
            </span>
          </div>
        </div>;
    default:
      return <div className="bg-gray-50 rounded-xl p-3 border border-gray-100" data-id="element-4263">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4264">
            <Package className="w-3.5 h-3.5 text-brand-600" data-id="element-4265" />
            Job Details
          </div>
          <div className="text-sm text-gray-700" data-id="element-4266">
            {(details as any).description}
          </div>
        </div>;
  }
}
function PricePanel({
  originalPrice,
  customerOffer
}: {
  originalPrice: number;
  customerOffer?: number;
}) {
  const hasCounter = typeof customerOffer === 'number' && customerOffer !== originalPrice;
  const delta = hasCounter ? (customerOffer as number) - originalPrice : 0;
  const deltaPct = hasCounter ? Math.round(delta / originalPrice * 100) : 0;
  const isLower = delta < 0;
  return <div className="mb-3" data-id="element-4267">
      <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-4268">
        {hasCounter ? 'Customer offer' : 'Price'}
      </div>
      <div className="flex items-baseline gap-2 flex-wrap" data-id="element-4269">
        <div className="text-xl font-display font-bold text-gray-900" data-id="element-4270">
          AED {(hasCounter ? customerOffer : originalPrice)?.toLocaleString()}
        </div>
        {hasCounter && <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full ${isLower ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`} data-id="element-4271">
            <TrendingDown className={`w-3 h-3 ${isLower ? '' : 'rotate-180'}`} data-id="element-4272" />
            {Math.abs(deltaPct)}%
          </span>}
      </div>
      {hasCounter && <div className="text-[11px] text-gray-500 mt-0.5" data-id="element-4273">
          was AED {originalPrice.toLocaleString()}
        </div>}
    </div>;
}
function PhotosStrip({
  photos,
  onOpen
}: {
  photos: string[];
  onOpen: (index: number) => void;
}) {
  return <div className="my-3" data-id="element-4274">
      <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-4275">
        <ImageIcon className="w-3 h-3" data-id="element-4276" />
        Customer photos
        <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600" data-id="element-4277">
          {photos.length}
        </span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1" data-id="element-4278">
        {photos.map((src, i) => <button key={i} type="button" onClick={() => onOpen(i)} className="group relative shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 hover:border-brand-300 transition-all" aria-label={`View photo ${i + 1}`} data-id="element-4279">
            <img src={src} alt={`Customer photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-4280" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center" data-id="element-4281">
              <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4282" />
            </div>
          </button>)}
      </div>
    </div>;
}
// ===================== Main Component =====================
export function SupplierRequests() {
  const navigate = useNavigate();
  const {
    requests,
    acceptRequest,
    declineRequest,
    updateBid,
    submitCounter
  } = useSupplierStore();
  const [tab, setTab] = useState<'incoming' | 'bidding' | 'history'>('incoming');
  const [counterOpenFor, setCounterOpenFor] = useState<string | null>(null);
  const incoming = requests.filter(r => r.status === 'pending' && r.type === 'direct');
  const bidding = requests.filter(r => r.type === 'bidding');
  // Lightbox state — keyed by request id
  const [lightbox, setLightbox] = useState<{
    requestId: string;
    index: number;
  } | null>(null);
  // AI chat state — request id currently open in the AI liaison modal
  const [aiChatRequestId, setAiChatRequestId] = useState<string | null>(null);
  const aiChatRequest = requests.find(r => r.id === aiChatRequestId) || null;
  const [expandedNegotiations, setExpandedNegotiations] = useState<Set<string>>(new Set());
  const toggleNegotiation = (id: string) => {
    setExpandedNegotiations(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const handleAccept = (id: string) => {
    const newJobId = acceptRequest(id);
    if (newJobId) {
      toast.success('Request accepted! Job created.');
      navigate(`/supplier/dispatcher?jobId=${newJobId}`);
    }
  };
  const handleDecline = (id: string) => {
    declineRequest(id);
    toast.success('Request declined.');
  };
  const handleUpdateBid = (id: string, currentBid?: number) => {
    const newBidStr = window.prompt('Enter your new bid (AED):', currentBid?.toString() || '');
    if (newBidStr) {
      const newBid = parseInt(newBidStr, 10);
      if (!isNaN(newBid)) {
        updateBid(id, newBid);
        toast.success('Bid updated successfully.');
      }
    }
  };
  const handleProceed = (id: string) => {
    navigate('/supplier/dispatcher');
  };
  const renderHeaderBar = (req: SupplierRequest, extra?: React.ReactNode) => {
    const isCounter = req.subtype === 'counter';
    const customerRounds = req.negotiation ? req.negotiation.filter(e => e.from === 'customer').length : 0;
    return <div className="flex items-center gap-3 mb-3 flex-wrap" data-id="element-4283">
        {req.type === 'direct' ? isCounter ? <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-4284">
              <Repeat className="w-3 h-3" data-id="element-4285" /> Counter-offer
              {customerRounds > 1 ? ` · round ${customerRounds}` : ''}
            </span> : <span className="bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-4286">
              New request
            </span> : <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${req.status === 'awarded' ? 'bg-green-50 text-green-700' : 'bg-brand-50 text-brand-700'}`} data-id="element-4287">
            {req.status === 'awarded' ? 'Awarded' : 'Live Bidding'}
          </span>}
        <span className="text-xs font-medium text-gray-500 flex items-center gap-1" data-id="element-4288">
          <Clock className="w-3.5 h-3.5" data-id="element-4289" /> {req.requestedTime}
        </span>
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-4290">
          {req.id}
        </span>
        {extra}
        <button onClick={() => setAiChatRequestId(req.id)} className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gradient-to-r from-brand-500 to-purple-500 hover:from-brand-600 hover:to-purple-600 text-white text-[11px] font-bold transition-all shadow-sm" data-id="element-4291">
          <Sparkles className="w-3 h-3" data-id="element-4292" /> Ask customer via AI
        </button>
      </div>;
  };
  return <SupplierLayout data-id="element-4293">
      <div className="max-w-5xl mx-auto" data-id="element-4294">
        <div className="flex items-center justify-between mb-6" data-id="element-4295">
          <h1 className="text-2xl font-display font-bold text-gray-900" data-id="element-4296">
            Requests & Bids
          </h1>
        </div>

        <div className="flex gap-2 mb-6 border-b border-gray-200 pb-px" data-id="element-4297">
          <button onClick={() => setTab('incoming')} className={`px-4 py-2 font-bold text-sm border-b-2 transition-colors ${tab === 'incoming' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-id="element-4298">
            Incoming Requests ({incoming.length})
          </button>
          <button onClick={() => setTab('bidding')} className={`px-4 py-2 font-bold text-sm border-b-2 transition-colors ${tab === 'bidding' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-id="element-4299">
            Active Bidding ({bidding.length})
          </button>
          <button onClick={() => setTab('history')} className={`px-4 py-2 font-bold text-sm border-b-2 transition-colors ${tab === 'history' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-id="element-4300">
            History
          </button>
        </div>

        <div className="space-y-4" data-id="element-4301">
          {tab === 'incoming' && incoming.map(req => <div key={req.id} className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4" data-id="element-4302">
                <div className="flex-1 min-w-0" data-id="element-4303">
                  {renderHeaderBar(req)}
                  <h3 className="font-bold text-gray-900 text-base mb-2" data-id="element-4304">
                    {req.serviceType}
                  </h3>

                  <div className="mb-2" data-id="element-4305">
                    <JobDetailsBlock details={req.jobDetails} data-id="element-4306" />
                  </div>

                  {req.photos && req.photos.length > 0 && <PhotosStrip photos={req.photos} onOpen={i => setLightbox({
              requestId: req.id,
              index: i
            })} data-id="element-4307" />}

                  {req.subtype === 'counter' && req.negotiation && req.negotiation.length > 0 && <div className="my-3 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-white overflow-hidden" data-id="element-4308">
                        <button onClick={() => toggleNegotiation(req.id)} className="w-full flex items-center gap-2 p-4 hover:bg-amber-50/40 transition-colors text-left" aria-expanded={expandedNegotiations.has(req.id)} data-id="element-4309">
                          <div className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0" data-id="element-4310">
                            <Repeat className="w-4 h-4" data-id="element-4311" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-4312">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700" data-id="element-4313">
                              Negotiation history
                            </p>
                            <p className="text-xs text-gray-600" data-id="element-4314">
                              Original quote AED{' '}
                              {req.originalPrice.toLocaleString()} ·{' '}
                              {req.negotiation.length} rounds · You countered{' '}
                              {req.negotiation.filter(e => e.from === 'supplier').length}{' '}
                              time(s)
                            </p>
                          </div>
                          <motion.div animate={{
                  rotate: expandedNegotiations.has(req.id) ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} className="shrink-0" data-id="element-4315">
                            <ChevronDown className="w-4 h-4 text-amber-700" data-id="element-4316" />
                          </motion.div>
                        </button>
                        <AnimatePresence initial={false} data-id="element-4317">
                          {expandedNegotiations.has(req.id) && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="overflow-hidden" data-id="element-4318">
                              <div className="px-4 pb-4 pt-1 border-t border-amber-200/60" data-id="element-4319">
                                <CounterEntry entries={req.negotiation.map((e, i, arr) => ({
                      name: e.from === 'customer' ? req.customerName : 'You',
                      amount: e.amount,
                      delta: i > 0 ? e.amount - arr[i - 1].amount : undefined,
                      timestamp: e.at,
                      note: e.note
                    }))} data-id="element-4320" />
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>}

                  <div className="flex items-center gap-2 text-xs font-medium text-gray-700 flex-wrap" data-id="element-4321">
                    <Navigation className="w-3 h-3 text-brand-500 shrink-0" data-id="element-4322" />
                    <span className="truncate" data-id="element-4323">{req.pickup}</span>
                    <ArrowRight className="w-3 h-3 text-gray-400 shrink-0" data-id="element-4324" />
                    <span className="truncate" data-id="element-4325">{req.dropoff}</span>
                  </div>
                </div>

                <div className="md:w-48 shrink-0 flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0 md:pl-4" data-id="element-4326">
                  <PricePanel originalPrice={req.originalPrice} customerOffer={req.customerOffer} data-id="element-4327" />
                  <div className="flex gap-2 mt-2" data-id="element-4328">
                    <button onClick={() => handleDecline(req.id)} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors flex items-center justify-center gap-1 text-xs" aria-label="Decline request" data-id="element-4329">
                      <X className="w-3.5 h-3.5" data-id="element-4330" /> Decline
                    </button>
                    <button onClick={() => handleAccept(req.id)} className="flex-1 py-2 bg-gray-900 hover:bg-black text-white rounded-lg font-bold transition-colors flex items-center justify-center gap-1 text-xs" aria-label="Accept request" data-id="element-4331">
                      <Check className="w-3.5 h-3.5" data-id="element-4332" /> Accept
                    </button>
                  </div>
                  {req.subtype === 'counter' && <button onClick={() => setCounterOpenFor(cur => cur === req.id ? null : req.id)} className="mt-2 w-full py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-lg font-bold transition-colors flex items-center justify-center gap-1 text-[11px] border border-amber-200" data-id="element-4333">
                      <Repeat className="w-3 h-3" data-id="element-4334" />
                      {counterOpenFor === req.id ? 'Close counter' : 'Send counter'}
                    </button>}
                  <AnimatePresence data-id="element-4335">
                    {counterOpenFor === req.id && <CounterInput defaultAmount={Math.round(((req.customerOffer || req.originalPrice) + req.originalPrice) / 2)} onCancel={() => setCounterOpenFor(null)} onSubmit={(amount, note) => {
                submitCounter(req.id, amount, note);
                setCounterOpenFor(null);
                toast.success(`Counter sent · AED ${amount.toLocaleString()}`);
              }} data-id="element-4336" />}
                  </AnimatePresence>
                </div>
              </div>)}

          {tab === 'bidding' && bidding.map(req => <div key={req.id} className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4" data-id="element-4337">
                <div className="flex-1 min-w-0" data-id="element-4338">
                  {renderHeaderBar(req, req.expiresIn ? <span className="text-xs font-medium text-orange-600 flex items-center gap-1" data-id="element-4339">
                        <Clock className="w-3 h-3" data-id="element-4340" /> Ends in {req.expiresIn}
                      </span> : undefined)}
                  <h3 className="font-bold text-gray-900 text-base mb-2" data-id="element-4341">
                    {req.serviceType}
                  </h3>

                  <div className="mb-2" data-id="element-4342">
                    <JobDetailsBlock details={req.jobDetails} data-id="element-4343" />
                  </div>

                  {req.photos && req.photos.length > 0 && <PhotosStrip photos={req.photos} onOpen={i => setLightbox({
              requestId: req.id,
              index: i
            })} data-id="element-4344" />}

                  <div className="flex items-center gap-2 text-xs font-medium text-gray-700 flex-wrap" data-id="element-4345">
                    <Navigation className="w-3 h-3 text-gray-900 shrink-0" data-id="element-4346" />
                    <span className="truncate" data-id="element-4347">{req.pickup}</span>
                    <ArrowRight className="w-3 h-3 text-gray-400 shrink-0" data-id="element-4348" />
                    <span className="truncate" data-id="element-4349">{req.dropoff}</span>
                  </div>
                </div>

                <div className="md:w-40 shrink-0 flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0 md:pl-4" data-id="element-4350">
                  <div className="mb-2" data-id="element-4351">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-0.5" data-id="element-4352">
                      Leading Bid
                    </p>
                    <p className="text-lg font-display font-bold text-gray-900" data-id="element-4353">
                      AED {req.leadingBid?.toLocaleString()}
                    </p>
                  </div>
                  <div className="mb-3" data-id="element-4354">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-brand-600 mb-0.5" data-id="element-4355">
                      Your Bid
                    </p>
                    <p className="text-lg font-display font-bold text-brand-600" data-id="element-4356">
                      AED {req.myBid?.toLocaleString()}
                    </p>
                  </div>
                  {req.status === 'pending' && <button onClick={() => handleUpdateBid(req.id, req.myBid)} className="w-full py-2 bg-gray-900 hover:bg-gray-700 text-white rounded-lg font-bold text-xs transition-colors" data-id="element-4357">
                      Update Bid
                    </button>}
                  {req.status === 'awarded' && <button onClick={() => handleProceed(req.id)} className="w-full py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold text-xs transition-colors" data-id="element-4358">
                      Proceed to Job
                    </button>}
                </div>
              </div>)}

          {tab === 'history' && <div className="bg-white rounded-2xl p-12 border border-gray-200 shadow-sm text-center" data-id="element-4359">
              <p className="text-sm text-gray-500" data-id="element-4360">No past requests yet.</p>
            </div>}
        </div>
      </div>

      {/* Photo Lightbox */}
      <AnimatePresence data-id="element-4361">
        {lightbox && (() => {
        const req = requests.find(r => r.id === lightbox.requestId);
        if (!req || !req.photos) return null;
        return <PhotoLightbox photos={req.photos} index={lightbox.index} setIndex={i => setLightbox({
          ...lightbox,
          index: i
        })} onClose={() => setLightbox(null)} data-id="element-4362" />;
      })()}
      </AnimatePresence>

      {/* AI-mediated chat */}
      <AnimatePresence data-id="element-4363">
        {aiChatRequest && <AskCustomerAiSlider open={!!aiChatRequest} customerName={aiChatRequest?.customerName || ''} contextLabel={aiChatRequest?.id} onClose={() => setAiChatRequestId(null)} data-id="element-4364" />}
      </AnimatePresence>
    </SupplierLayout>;
}
// REMOVED_DUPLICATE_BLOCK_BELOW
function _unused_DuplicateBlock_DELETE_ME({
  entries
}: {
  entries: Array<{
    name: string;
    amount: number;
    delta?: number;
    timestamp: string;
    note?: string;
  }>;
}) {
  const [expanded, setExpanded] = useState(false);
  return <div className="border-l border-gray-300 max-w-md mx-auto relative pl-6 space-y-3 before:absolute before:left-[11px] before:top-1 before:bottom-1 before:w-px before:bg-amber-200" data-id="element-4365">
      {entries.map((entry, index) => {
      // Determine previous entry delta for delta pill
      const prevEntry = index > 0 ? entries[index - 1] : null;
      const deltaValue = prevEntry && entry.delta !== undefined ? entry.delta : null;
      const showDelta = deltaValue !== null && deltaValue !== 0;
      return <li key={index} className="relative" data-id="element-4366">
            {/* Avatar */}
            <div className="absolute -left-6 top-1 w-5 h-5 ring-2 ring-white rounded-full flex items-center justify-center font-semibold text-xs text-gray-700 bg-brand-100" data-id="element-4367">
              {entry.name.charAt(0).toUpperCase()}
            </div>
            {/* Row */}
            <div className="flex items-center p-2 pl-8 bg-white rounded-lg shadow-sm space-x-2 cursor-pointer" onClick={() => setExpanded(!expanded)} data-id="element-4368">
              <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:justify-between items-center sm:items-center w-full" data-id="element-4369">
                <div className="flex items-center space-x-2" data-id="element-4370">
                  <p className="font-medium" data-id="element-4371">
                    {entry.name} countered AED {entry.amount.toLocaleString()}
                  </p>
                  {/* Delta pill if delta exists and not zero */}
                  {showDelta && <div className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${deltaValue! > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-4372">
                      {deltaValue! > 0 ? '+' : ''}
                      {deltaValue} AED
                    </div>}
                </div>
                <span className="text-[10px] text-gray-500" data-id="element-4373">
                  {entry.timestamp}
                </span>
              </div>
            </div>
            {/* Note */}
            {entry.note && <blockquote className="mt-1 ml-8 text-gray-600 italic text-[11px] border-l-2 border-gray-300 pl-2" data-id="element-4374">
                "{entry.note}"
              </blockquote>}
          </li>;
    })}
    </div>;
}
/* DUPLICATE — renamed to avoid identifier collision */
function _unused_CounterInput_DELETE_ME({
  defaultAmount,
  onCancel,
  onSubmit
}: {
  defaultAmount: number;
  onCancel: () => void;
  onSubmit: (amount: number, note?: string) => void;
}) {
  const [amount, setAmount] = useState<string>(defaultAmount.toString());
  const [note, setNote] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleSend = () => {
    const parsed = parseInt(amount, 10);
    if (!isNaN(parsed) && parsed > 0) {
      onSubmit(parsed, note.trim() || undefined);
    }
  };
  useEffect(() => {
    // focus input field at mount
    inputRef.current?.focus();
  }, []);
  return <motion.div initial={{
    opacity: 0,
    y: -4
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -4
  }} className="max-w-sm mx-auto bg-gray-50 rounded-lg p-3 shadow-md space-y-2" data-id="element-4375">
      {/* Amount input */}
      <div className="flex items-center border border-gray-300 rounded-full px-2 py-1 bg-gray-50" data-id="element-4376">
        <span className="text-gray-500 font-semibold pr-2" data-id="element-4377">AED</span>
        <input ref={inputRef} type="number" inputMode="numeric" pattern="\d*" className="flex-1 bg-transparent outline-none text-gray-700 text-xl font-semibold px-2 py-0.5" value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} min={1} autoFocus data-id="element-4378" />
      </div>
      {/* Note input */}
      <input type="text" placeholder="Optional note (e.g. Final offer with 2 movers)" className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" value={note} onChange={e => setNote(e.target.value)} data-id="element-4379" />
      {/* Buttons */}
      <div className="flex justify-between mt-2 space-x-2" data-id="element-4380">
        <button className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors" onClick={onCancel} data-id="element-4381">
          Cancel
        </button>
        <button className="flex-1 bg-gray-900 text-white rounded-full px-4 py-2 hover:bg-gray-700 transition-colors" onClick={handleSend} data-id="element-4382">
          Send counter
        </button>
      </div>
    </motion.div>;
}